import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Account, Tenant } from 'src/models/account.model';
import { AuthService } from 'src/services/auth.service';
import { TenantService } from 'src/services/tenant.service';
import { VoucherService } from 'src/services/voucher.service';

interface TenantSession {
  tenant_id: string;
  account_key: string;
  store_name: string;
  tenant_name: string;
  username: string;
  logged_in_at: Date;
  expires_at: Date;
}

@Component({
  selector: 'app-tenant-dashboard',
  templateUrl: './tenant-dashboard.component.html',
  styleUrls: ['./tenant-dashboard.component.css']
})
export class TenantDashboardComponent implements OnInit {

  tenantSession: TenantSession | null = null;
  account: Account | null = null;
  tenant: Tenant | null = null;
  isLoading: boolean = true;

  voucherStats = {
    total: 0,
    active: 0,
    used: 0,
    expired: 0
  };

  // Voucher count for current period (used for limit checking)
  periodVoucherCount: number = 0;

  constructor(
    private router: Router,
    private authService: AuthService,
    private tenantService: TenantService,
    private voucherService: VoucherService
  ) {}

  async ngOnInit() {
    await this.loadData();
  }

  async loadData() {
    this.isLoading = true;

    const sessionStr = localStorage.getItem('tenant_session');
    if (!sessionStr) {
      this.router.navigate(['/']);
      return;
    }

    this.tenantSession = JSON.parse(sessionStr);

    try {
      // Load account
      this.account = await this.authService.getAccountByKey(this.tenantSession.account_key);

      if (this.account) {
        // Get tenant details
        this.tenant = this.tenantService.getTenantById(this.account, this.tenantSession.tenant_id);

        // Load voucher stats
        this.voucherStats = await this.voucherService.getVoucherStats(this.tenantSession.tenant_id);

        // Get period-based count for limit checking
        const resetPeriod = this.tenant?.voucher_settings?.reset_period;
        this.periodVoucherCount = await this.voucherService.getVoucherCountForPeriod(this.tenantSession.tenant_id, resetPeriod);
      }
    } catch (error) {
      console.error('Error loading data:', error);
    }

    this.isLoading = false;
  }

  getVouchersGenerated(): number {
    // Return period-based count for the limit display
    return this.periodVoucherCount;
  }

  getTotalVouchers(): number {
    // Return total all-time vouchers
    return this.voucherStats.total;
  }

  getVoucherLimit(): string {
    if (this.tenant?.voucher_settings?.generation_limit === null ||
        this.tenant?.voucher_settings?.generation_limit === undefined) {
      return 'Unlimited';
    }
    return this.tenant.voucher_settings.generation_limit.toString();
  }

  getRemainingVouchers(): string {
    const settings = this.tenant?.voucher_settings;
    if (!settings || settings.generation_limit === null || settings.generation_limit === undefined) {
      return 'Unlimited';
    }
    // Use period-based count for remaining calculation
    const remaining = settings.generation_limit - this.periodVoucherCount;
    return remaining > 0 ? remaining.toString() : '0';
  }

  canGenerateVouchers(): boolean {
    if (!this.tenant) return false;
    // Use period-based count from Firebase for limit checking
    const settings = this.tenant.voucher_settings;
    if (!settings || settings.generation_limit === null || settings.generation_limit === undefined) {
      return true;
    }
    const remaining = settings.generation_limit - this.periodVoucherCount;
    return remaining > 0;
  }

  goToGenerate() {
    this.router.navigate([`/tenant/${this.tenantSession.account_key}/generate`]);
  }

  goToVouchers() {
    this.router.navigate([`/tenant/${this.tenantSession.account_key}/vouchers`]);
  }

  getResetPeriodLabel(): string {
    const resetPeriod = this.tenant?.voucher_settings?.reset_period;
    return resetPeriod === 'daily' ? 'Daily' : 'Lifetime';
  }

  hasResetPeriod(): boolean {
    return this.tenant?.voucher_settings?.reset_period === 'daily';
  }
}
